import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { FeatureItem } from './AssetGrid/utils/types';
import type { SelectedAsset } from '../types';
import { getAssetWithExtendedData } from '../redux/asset/actions';
import { getInspections } from '../redux/inspections/actions';
import { getRiskScore } from '../redux/risk/actions';
import { normalizeSelectionType } from '../utils/assetCategory';

// Features #32–33: property pane open/close, multi-asset navigation
//
// This hook manages state and dispatches data-loading actions.
// The PropertyPane UI component is rendered separately by the consuming app.

interface PropertyPaneInit {
	deselectAssets?: (assets: string[]) => void;
	highlightAssets?: (assets: string[]) => void;
	zoomToAssets?: (assets: string[]) => void;
	traceFromAsset?: (assetId: string, dsTrace?: boolean) => void;
}

/** Converts hit-test SelectedAsset results into FeatureItems for the property pane. */
export const selectedAssetsToFeatureItems = (
	assets: SelectedAsset[],
): FeatureItem[] =>
	assets.map(a => {
		const upstreamType = (a as SelectedAsset & { type?: string }).type;
		return {
			id: a.id,
			type: normalizeSelectionType(upstreamType, a.assetType),
			assetId: a.assetId ?? a.id,
			assetType: a.assetType,
			layerId: a.layerId,
			layerName: a.layerName,
			name: (a as SelectedAsset & { name?: string }).name,
			facilityType: (a as SelectedAsset & { facilityType?: string }).facilityType,
			workspaceId: (a as SelectedAsset & { workspaceId?: string }).workspaceId,
			// IncidentProperties renders `sequence` as both the card title and the
			// Incident ID, so it has to survive the FeatureItem conversion — without
			// it the Incidents tab shows "undefined" (I360P-1354).
			sequence: (a as SelectedAsset & { sequence?: number }).sequence,
			sensorId: (a as SelectedAsset & { sensorId?: string }).sensorId,
			priority: (a as SelectedAsset & { priority?: string }).priority,
			createDate: (a as SelectedAsset & { createDate?: string }).createDate,
		};
	});

export const usePropertyPane = (init: PropertyPaneInit = {}) => {
	const { deselectAssets, highlightAssets, zoomToAssets, traceFromAsset } =
		init;

	const dispatch = useDispatch();
	const [isOpen, setIsOpen] = useState(false);
	const [assets, setAssets] = useState<FeatureItem[]>([]);
	const [activeIndex, setActiveIndex] = useState(0);

	const openPropertyPane = useCallback(
		(incoming: FeatureItem | FeatureItem[]): void => {
			const list = Array.isArray(incoming) ? incoming : [incoming];
			setAssets(list);
			setActiveIndex(0);
			setIsOpen(true);
		},
		[],
	);

	const closePropertyPane = useCallback((): void => {
		setIsOpen(false);
		setAssets([]);
		setActiveIndex(0);
	}, []);

	return {
		isOpen,
		assets,
		activeIndex,
		items: assets,
		deselectAssets,
		highlightAssets,
		zoomToAssets,
		traceFromAsset,
		openPropertyPane,
		closePropertyPane,

		nextAsset(): void {
			setActiveIndex(prev => Math.min(prev + 1, assets.length - 1));
		},

		previousAsset(): void {
			setActiveIndex(prev => Math.max(prev - 1, 0));
		},

		setActiveAsset(index: number): void {
			if (index >= 0 && index < assets.length) {
				setActiveIndex(index);
			}
		},

		loadAssetProperties(assetId: string): void {
			dispatch(getAssetWithExtendedData(assetId));
		},

		loadInspectionData(assetId: string): void {
			dispatch(getInspections(assetId));
		},

		loadRiskData(assetId: string): void {
			dispatch(getRiskScore(assetId));
		},
	};
};

export type PropertyPane = ReturnType<typeof usePropertyPane>;
